import { useState, useEffect, useCallback, useRef } from 'react';
import { api, GrTimePlayedAggregates } from '@/lib/api';

interface UseTimePlayedReturn {
  data: GrTimePlayedAggregates | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useTimePlayed(): UseTimePlayedReturn {
  const [data, setData] = useState<GrTimePlayedAggregates | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetch = useCallback(async () => {
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      const result = await api.getGameRewardsTimePlayed();
      if (fetchIdRef.current !== id) return;
      setData(result);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load time played data');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, []);

  const refresh = useCallback(() => {
    setError(null);
    fetch();
  }, [fetch]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return { data, loading, error, refresh };
}
